import { useEffect, useState } from "react";
import { API_URL } from "../../configs";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Typography,
} from "@mui/material";
import { Google } from "@mui/icons-material";
import useGlobalStore from "../../store";

const GoogleLogin = () => {
  const { showGDriveLoginError, setShowGDriveLoginError } = useGlobalStore();

  const [authUrl, setAuthUrl] = useState("");

  useEffect(() => {
    // Fetch the authorization URL from your backend
    fetch(`${API_URL}/auth/get-auth-url`)
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        setAuthUrl(data?.data?.auth_url);
      })
      .catch((error) => {
        console.error("Error fetching auth URL:", error);
      });
  }, []);

  const handleCallback = async (code: string) => {
    try {
      // Exchange the code for tokens
      await fetch(`${API_URL}/auth/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code }),
      });
    } catch (error) {
      console.error("Error handling callback:", error);
    }
  };

  useEffect(() => {
    // Listen for messages from popup window
    const messageHandler = (event: MessageEvent) => {
      if (event.data.type === "GOOGLE_LOGIN_SUCCESS") {
        const code = event.data.code;
        if (code) {
          handleCallback(code);
        }
      }
    };

    window.addEventListener("message", messageHandler);
    return () => window.removeEventListener("message", messageHandler);
  }, []);

  const handleLogin = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!authUrl) {
      console.error("Auth URL is not available yet");
      return;
    }

    // Set dimensions for the popup window
    const width = 500;
    const height = 700; // Slightly taller to accommodate Google's login flow
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    // Configure window features for a clean popup experience
    const features = [
      `width=${width}`,
      `height=${height}`,
      `left=${left}`,
      `top=${top}`,
      "resizable=yes",
      "scrollbars=yes",
      "status=yes",
      "toolbar=no",
      "menubar=no",
    ].join(",");

    // Open the popup and store its reference
    const popup = window.open(authUrl, "GoogleDriveLogin", features);

    // Focus the popup and check if it was blocked
    if (popup) {
      // Popup opened successfully
      popup.focus();
    } else {
      window.open(authUrl, "_blank", "noopener,noreferrer");
    }

    setShowGDriveLoginError(false);
  };

  return (
    <>
      <Button
        onClick={handleLogin}
        variant="contained"
        color="primary"
        startIcon={<Google />}
      >
        Sign in with Google
      </Button>

      <Dialog
        open={showGDriveLoginError}
        onClose={() => setShowGDriveLoginError(false)}
        aria-labelledby="gdrive-login-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="gdrive-login-dialog-title">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">Google Drive Access Required</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            To access Fashion Words files and data, you need to
            sign in with Google Drive. This allows the
            application to access batches, master files and images.
          </DialogContentText>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: 2,
              bgcolor: "#f5f5f5",
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
              How to proceed:
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              1. Click the "Sign in with Google" button below
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              2. Select your Google account and grant the required
              permissions
            </Typography>
            <Typography variant="body2">
              3. After successful login, return to this application and reload
              the page (F5)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setShowGDriveLoginError(false)}
            color="inherit"
          >
            Close
          </Button>
          <Button
            onClick={handleLogin}
            variant="contained"
            color="primary"
            startIcon={<Google />}
          >
            Sign in with Google
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GoogleLogin;
